import React, { useState, useEffect } from "react";
import {
    saveOrganisationOnboarding,
    saveOnboardingContact,
    addOnboardingDocument,
    removeOnboardingDocument,
    submitOnboarding,
} from "../../UrlCrud";
import Upload from "../../Upload";
import Select from "react-select";
import stateList from "../../../nigeria-state-and-lgas.json";
import "./onboarding.scss";

const STEPS = ["Organisation", "Verification", "Primary Contact", "Documents", "Review"];

// Organisation onboarding - a progressive, multi-step flow (per the Account
// -> Organisation -> Verification -> Review journey). Each step saves
// independently (PUT /onboarding/organisation, POST /onboarding/documents)
// so nothing is lost if the user leaves and comes back - `onRefresh` is
// called after every save to keep the parent's status/checklist current.
export default function OrganisationOnboarding({ user, company, documentRequirements, onSubmitted, onRefresh, initialStep }) {
    const [step, setStep] = useState(initialStep || 0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [orgInfo, setOrgInfo] = useState({
        name: company?.name || "",
        address: company?.address || "",
        website: company?.website || "",
        email: company?.email || "",
    });
    const [stateValue, setStateValue] = useState(company?.state || null);
    const [lgaValue, setLgaValue] = useState(company?.lga || null);

    const [regDetails, setRegDetails] = useState(company?.registrationDetails || {
        registrationNumber: "",
        registrationCountry: "",
        taxId: "",
    });

    const [contact, setContact] = useState({
        telephone: user?.telephone || "",
        designation: user?.designation || "",
        address: user?.address || "",
    });

    const [docType, setDocType] = useState(null);
    const [docFile, setDocFile] = useState(null);
    const [docUploading, setDocUploading] = useState(false);

    const requirements = documentRequirements || [];
    const uploadedDocs = company?.documents || [];

    useEffect(() => {
        setOrgInfo({
            name: company?.name || "",
            address: company?.address || "",
            website: company?.website || "",
            email: company?.email || "",
        });
        setStateValue(company?.state || null);
        setLgaValue(company?.lga || null);
        setRegDetails(company?.registrationDetails || { registrationNumber: "", registrationCountry: "", taxId: "" });
    }, [company]);

    const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
    const back = () => setStep(s => Math.max(s - 1, 0));

    const saveOrgInfo = async () => {
        setSaving(true);
        setError(null);
        try {
            await saveOrganisationOnboarding({
                name: orgInfo.name,
                address: orgInfo.address,
                website: orgInfo.website,
                email: orgInfo.email,
                state: stateValue,
                lga: lgaValue,
            });
            await onRefresh();
            next();
        } catch (err) {
            setError(err.response && err.response.data ? err.response.data : "Failed to save organisation information");
        } finally {
            setSaving(false);
        }
    };

    const saveVerificationInfo = async () => {
        setSaving(true);
        setError(null);
        try {
            await saveOrganisationOnboarding({
                name: orgInfo.name,
                address: orgInfo.address,
                website: orgInfo.website,
                email: orgInfo.email,
                state: stateValue,
                lga: lgaValue,
                registrationDetails: regDetails,
            });
            await onRefresh();
            next();
        } catch (err) {
            setError(err.response && err.response.data ? err.response.data : "Failed to save verification information");
        } finally {
            setSaving(false);
        }
    };

    const saveContact = async () => {
        setSaving(true);
        setError(null);
        try {
            // Uses the dedicated self-service onboarding endpoint - NOT
            // editUser()/PUT /v2/user/edit/:id, which requires the
            // `manageUser` permission that a pending-onboarding user never
            // has (roles are only assigned on approval), so it 401'd here.
            await saveOnboardingContact({ telephone: contact.telephone, designation: contact.designation, address: contact.address });
            await onRefresh();
            next();
        } catch (err) {
            setError("Failed to save contact information");
        } finally {
            setSaving(false);
        }
    };

    const uploadDocument = async () => {
        if (!docType || !docFile) return;
        setDocUploading(true);
        setError(null);
        try {
            const url = await Upload(docFile);
            if (!url) throw new Error("Upload failed");
            await addOnboardingDocument({ type: docType.type, label: docType.label, url });
            await onRefresh();
            setDocType(null);
            setDocFile(null);
        } catch (err) {
            setError("Failed to upload document. Please try again.");
        } finally {
            setDocUploading(false);
        }
    };

    const deleteDocument = async (docId) => {
        await removeOnboardingDocument(docId);
        await onRefresh();
    };

    const missingRequired = requirements
        .filter(r => r.required)
        .filter(r => !uploadedDocs.some(d => d.type === r.type));

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);
        try {
            await submitOnboarding();
            onSubmitted();
        } catch (err) {
            setError(err.response && err.response.data ? (err.response.data.message || err.response.data) : "Failed to submit for review");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="onboarding-card">
            <div className="onboarding-steps">
                {STEPS.map((label, i) => (
                    <div key={label} className={`onboarding-step ${i === step ? "onboarding-step--active" : i < step ? "onboarding-step--done" : ""}`}>
                        <span className="onboarding-step__number">{i + 1}</span>
                        <span className="onboarding-step__label">{label}</span>
                    </div>
                ))}
            </div>

            {error && (
                <div className="alert alert-custom alert-light-danger fade show mb-6">
                    <div className="alert-text font-weight-bold">{typeof error === "string" ? error : JSON.stringify(error)}</div>
                </div>
            )}

            {step === 0 && (
                <>
                    <h4 className="font-weight-bolder mb-2">Organisation Information</h4>
                    <p className="text-muted mb-6">Tell us about your organisation - this helps us set up your account correctly.</p>
                    <div className="form-group">
                        <label>Organisation name</label>
                        <input className="form-control form-control-solid" value={orgInfo.name} onChange={e => setOrgInfo({ ...orgInfo, name: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>Address</label>
                        <input className="form-control form-control-solid" value={orgInfo.address} onChange={e => setOrgInfo({ ...orgInfo, address: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>State</label>
                        <Select placeholder="Select State" options={stateList} getOptionValue={o => o["alias"]} getOptionLabel={o => o["state"]} value={stateValue} onChange={setStateValue} />
                    </div>
                    <div className="form-group">
                        <label>LGA</label>
                        <Select placeholder="LGA" options={stateValue && stateValue.lgas ? stateValue.lgas.map(p => ({ name: p, label: p })) : []} getOptionValue={o => o["name"]} getOptionLabel={o => o["label"]} value={lgaValue} onChange={setLgaValue} />
                    </div>
                    <div className="form-group">
                        <label>Website (optional)</label>
                        <input className="form-control form-control-solid" value={orgInfo.website} onChange={e => setOrgInfo({ ...orgInfo, website: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>Organisation email (optional)</label>
                        <input className="form-control form-control-solid" value={orgInfo.email} onChange={e => setOrgInfo({ ...orgInfo, email: e.target.value })} />
                    </div>
                    <div className="onboarding-footer-actions">
                        <button className="btn btn-primary" disabled={saving || !orgInfo.name} onClick={saveOrgInfo}>Save &amp; Continue</button>
                    </div>
                </>
            )}

            {step === 1 && (
                <>
                    <h4 className="font-weight-bolder mb-2">Registration / Verification Information</h4>
                    <p className="text-muted mb-6">Optional, but speeds up review - provide whatever official registration details you have.</p>
                    <div className="form-group">
                        <label>Registration number</label>
                        <input className="form-control form-control-solid" value={regDetails.registrationNumber || ""} onChange={e => setRegDetails({ ...regDetails, registrationNumber: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>Country of registration</label>
                        <input className="form-control form-control-solid" value={regDetails.registrationCountry || ""} onChange={e => setRegDetails({ ...regDetails, registrationCountry: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>Tax ID (optional)</label>
                        <input className="form-control form-control-solid" value={regDetails.taxId || ""} onChange={e => setRegDetails({ ...regDetails, taxId: e.target.value })} />
                    </div>
                    <div className="onboarding-footer-actions">
                        <button className="btn btn-secondary" onClick={back}>Back</button>
                        <button className="btn btn-primary" disabled={saving} onClick={saveVerificationInfo}>Save &amp; Continue</button>
                    </div>
                </>
            )}

            {step === 2 && (
                <>
                    <h4 className="font-weight-bolder mb-2">Primary Contact / Administrator</h4>
                    <p className="text-muted mb-6">That's you, {user?.firstName} - just confirm your contact details.</p>
                    <div className="form-group">
                        <label>Phone number</label>
                        <input className="form-control form-control-solid" value={contact.telephone} onChange={e => setContact({ ...contact, telephone: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>Your designation (e.g. Facility Manager)</label>
                        <input className="form-control form-control-solid" value={contact.designation} onChange={e => setContact({ ...contact, designation: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>Your address</label>
                        <input className="form-control form-control-solid" value={contact.address} onChange={e => setContact({ ...contact, address: e.target.value })} />
                    </div>
                    <div className="onboarding-footer-actions">
                        <button className="btn btn-secondary" onClick={back}>Back</button>
                        <button className="btn btn-primary" disabled={saving} onClick={saveContact}>Save &amp; Continue</button>
                    </div>
                </>
            )}

            {step === 3 && (
                <>
                    <h4 className="font-weight-bolder mb-2">Supporting Documents</h4>
                    <p className="text-muted mb-6">
                        Only "{requirements.find(r => r.required)?.label}" is required to submit - the rest help speed up review.
                    </p>

                    {uploadedDocs.map(doc => (
                        <div className="onboarding-document-row" key={doc._id}>
                            <span className="onboarding-document-row__label">{doc.label}</span>
                            <span className={`label label-inline label-light-${doc.status === "approved" ? "success" : doc.status === "rejected" ? "danger" : "warning"}`}>
                                {doc.status}
                            </span>
                            {doc.status === "pending" && (
                                <button type="button" className="btn btn-sm btn-light-danger" onClick={() => deleteDocument(doc._id)}>Remove</button>
                            )}
                        </div>
                    ))}

                    <div className="form-group mt-6">
                        <label>Document type</label>
                        <Select
                            placeholder="Select document type"
                            options={requirements}
                            getOptionLabel={o => `${o.label}${o.required ? " (required)" : ""}`}
                            getOptionValue={o => o.type}
                            value={docType}
                            onChange={setDocType}
                        />
                    </div>
                    <div className="form-group">
                        <label>File</label>
                        {/* Plain native file input - NOT drag-and-drop only, so
                            mobile browsers/WebViews can open the normal
                            camera/file picker. */}
                        <input
                            type="file"
                            className="form-control"
                            accept="image/*,application/pdf"
                            onChange={e => setDocFile(e.target.files && e.target.files[0])}
                        />
                    </div>
                    <div className="onboarding-footer-actions">
                        <button className="btn btn-light-primary" disabled={!docType || !docFile || docUploading} onClick={uploadDocument}>
                            {docUploading ? "Uploading..." : "Upload document"}
                        </button>
                    </div>

                    <div className="onboarding-footer-actions">
                        <button className="btn btn-secondary" onClick={back}>Back</button>
                        <button className="btn btn-primary" onClick={next}>Continue</button>
                    </div>
                </>
            )}

            {step === 4 && (
                <>
                    <h4 className="font-weight-bolder mb-2">Review and Submit</h4>
                    <p className="text-muted mb-6">Double check everything below, then submit for review. You can't edit these while under review - but if changes are requested, you'll be able to come straight back to the relevant step.</p>

                    <div className="onboarding-document-row"><span className="onboarding-document-row__label">Organisation</span><span>{orgInfo.name}</span></div>
                    <div className="onboarding-document-row"><span className="onboarding-document-row__label">Address</span><span>{orgInfo.address || "-"}</span></div>
                    <div className="onboarding-document-row"><span className="onboarding-document-row__label">Documents uploaded</span><span>{uploadedDocs.length}</span></div>

                    {missingRequired.length > 0 && (
                        <div className="alert alert-custom alert-light-warning fade show my-6">
                            <div className="alert-text font-weight-bold">
                                Missing required document(s): {missingRequired.map(r => r.label).join(", ")}
                            </div>
                        </div>
                    )}

                    <div className="onboarding-footer-actions">
                        <button className="btn btn-secondary" onClick={back}>Back</button>
                        <button className="btn btn-success" disabled={saving || missingRequired.length > 0} onClick={handleSubmit}>
                            Submit for Review
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
